import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Eye,
  Loader,
  ChevronLeft,
  ChevronRight,
  Phone,
  FileText,
  History,
  X,
  Clock,
  TrendingUp,
  Users,
  Filter,
  ChevronDown,
  CalendarClock,
} from "lucide-react";
import StatCard from "../components/StatCard";
import ExpandableSearchBar from "../components/ExpandableSearchBar";
import {
  leadsApi,
  ordersApi,
  dashboardApi,
  leadDispositionApi,
} from "../services/api";
import Pagination from "../components/Pagination";
import NewLeadIcon from "../assets/Logo2.0Screen/New Lead.svg";
import OrderPlacedIcon from "../assets/Logo2.0Screen/Order Placed.svg";
import CallBackIcon from "../assets/Logo2.0Screen/Call back.svg";
import NotConnectedIcon from "../assets/Logo2.0Screen/Not Connected.svg";
import CallDisconnectedIcon from "../assets/Logo2.0Screen/Call disconnected.svg";
import NotInterestedIcon from "../assets/Logo2.0Screen/Not Interested.svg";
import FutureCallBackIcon from "../assets/Logo2.0Screen/Future Call back.svg";
import PrankCallIcon from "../assets/Logo2.0Screen/Prank call.svg";
import SwitchOffIcon from "../assets/Logo2.0Screen/Switch off.svg";
import { message, DatePicker } from "antd";
import dayjs from "dayjs";
import { formatDate, maskPhone, formatCurrency } from "../utils/helpers";
import { useTheme } from "../context/ThemeContext";

const { RangePicker } = DatePicker;

const DISPOSITION_CONFIG = {
  newLead: {
    icon: NewLeadIcon,
    theme: {
      bg: "bg-[#F8FAFF]",
      border: "border-[#E0E7FF]",
      inner: "shadow-[inset_0px_0px_15px_rgba(37,99,235,0.15)]",
      ring: "ring-blue-400",
    },
  },
  orderPlaced: {
    icon: OrderPlacedIcon,
    theme: {
      bg: "bg-[#ECFDF5]",
      border: "border-[#D1FAE5]",
      inner: "shadow-[inset_0px_0px_15px_rgba(5,150,105,0.15)]",
      ring: "ring-emerald-400",
    },
  },
  callBack: {
    icon: CallBackIcon,
    theme: {
      bg: "bg-[#FFFBEB]",
      border: "border-[#FEF3C7]",
      inner: "shadow-[inset_0px_0px_15px_rgba(217,119,6,0.15)]",
      ring: "ring-amber-400",
    },
  },
  notConnected: {
    icon: NotConnectedIcon,
    theme: {
      bg: "bg-[#F9FAFB]",
      border: "border-[#F3F4F6]",
      inner: "shadow-[inset_0px_0px_15px_rgba(55,65,81,0.15)]",
      ring: "ring-gray-400",
    },
  },
  callDisconnected: {
    icon: CallDisconnectedIcon,
    theme: {
      bg: "bg-[#FFF7ED]",
      border: "border-[#FFEDD5]",
      inner: "shadow-[inset_0px_0px_15px_rgba(234,88,12,0.15)]",
      ring: "ring-orange-400",
    },
  },
  notInterested: {
    icon: NotInterestedIcon,
    theme: {
      bg: "bg-[#FFF1F2]",
      border: "border-[#FFE4E6]",
      inner: "shadow-[inset_0px_0px_15px_rgba(225,29,72,0.15)]",
      ring: "ring-rose-400",
    },
  },
  futureCallBack: {
    icon: FutureCallBackIcon,
    theme: {
      bg: "bg-[#EFF6FF]",
      border: "border-[#DBEAFE]",
      inner: "shadow-[inset_0px_0px_15px_rgba(124,58,237,0.15)]",
      ring: "ring-blue-400",
    },
  },
  prankCall: {
    icon: PrankCallIcon,
    theme: {
      bg: "bg-[#FEF2F2]",
      border: "border-[#FEE2E2]",
      inner: "shadow-[inset_0px_0px_15px_rgba(220,38,38,0.15)]",
      ring: "ring-red-400",
    },
  },
  switchOff: {
    icon: SwitchOffIcon,
    theme: {
      bg: "bg-[#F5F3FF]",
      border: "border-[#EDE9FE]",
      inner: "shadow-[inset_0px_0px_15px_rgba(124,58,237,0.15)]",
      ring: "ring-purple-400",
    },
  },
  didNotOrder: {
    icon: NotInterestedIcon,
    theme: {
      bg: "bg-[#FFF1F2]",
      border: "border-[#FFE4E6]",
      inner: "shadow-[inset_0px_0px_15px_rgba(225,29,72,0.15)]",
      ring: "ring-rose-400",
    },
  },
};

const DEFAULT_CONFIG = {
  icon: NewLeadIcon,
  theme: {
    bg: "bg-[#F9FAFB]",
    border: "border-[#F3F4F6]",
    inner: "shadow-[inset_0px_0px_15px_rgba(55,65,81,0.15)]",
    ring: "ring-gray-400",
  },
};

function Dashboard() {
  const { currentTheme } = useTheme();
  const isDark =
    currentTheme.textColor === "#FFFFFF" || currentTheme.color === "#0f172a";
  const navigate = useNavigate();

  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [dispositionCards, setDispositionCards] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [openActionMenuId, setOpenActionMenuId] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
  const [dateRange, setDateRange] = useState("All Time");
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [totalItems, setTotalItems] = useState(0);

  // ✅ Dialer state — App.jsx ke FloatingDialer ko trigger karne ke liye
  // window event dispatch karenge taaki App-level FloatingDialer sun sake
  const triggerDialer = (lead) => {
    window.dispatchEvent(new CustomEvent("crm:dial", { detail: { lead } }));
  };

  const itemsPerPage = 10;
  const userId = 1;

  // ✅ Admin check: Admin should never access employee /dashboard
  useEffect(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      const role = (u.designation || u.role || "").toLowerCase();
      if (role.includes("admin")) {
        navigate("/dashboard/realtime", { replace: true });
      }
    } catch (e) {
      console.error(e);
    }
  }, [navigate]);

  const handleStatClick = (status) => {
    if (selectedStatus === status) {
      setSelectedStatus(null);
      fetchDashboardDataWithDisposition(null);
    } else {
      setSelectedStatus(status);
      const selectedCard = dispositionCards.find((c) => c.status === status);
      fetchDashboardDataWithDisposition(selectedCard?.dispositionId || null);
    }
  };

  useEffect(() => {
    if (selectedStatus !== null) {
      const selectedCard = dispositionCards.find(
        (c) => c.status === selectedStatus,
      );
      fetchDashboardDataWithDisposition(selectedCard?.dispositionId || null);
    } else {
      fetchDashboardData();
    }
  }, [currentPage]);

  const fetchDashboardDataWithDisposition = async (dispositionId = null) => {
    try {
      setLoading(true);
      const { startDate, endDate } = getDateRangeDates(dateRange);
      const user = JSON.parse(localStorage.getItem("user"));
      const employeeId = user?.id || userId;
      const params = new URLSearchParams();
      params.append("employeeId", employeeId);
      params.append("page", currentPage - 1);
      params.append("size", itemsPerPage);
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate);
      if (dispositionId) params.append("dispositionId", dispositionId);
      const query = `?${params.toString()}`;
      const leadsData = await leadsApi.getAllLeads(query).catch(() => []);
      const parsedLeads = Array.isArray(leadsData?.leads)
        ? leadsData.leads
        : Array.isArray(leadsData?.content)
          ? leadsData.content
          : Array.isArray(leadsData)
            ? leadsData
            : [];
      setLeads(parsedLeads);
      setTotalItems(leadsData?.totalCount || parsedLeads.length);
    } catch (err) {
      console.error("Error:", err);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchDispositions = async () => {
      try {
        const data = await leadDispositionApi.getAllDispositions();
        const active = data
          .filter((d) => d.status === true)
          .map((d) => ({ ...d, dispositionId: d.dispositionId || d.id }));
        setLeadStatuses(active);
      } catch (error) {
        console.error("Error fetching dispositions:", error);
        setLeadStatuses([
          { dispositionId: 4, dispositionName: "NEW LEAD" },
          { dispositionId: 5, dispositionName: "ORDER PLACED" },
          { dispositionId: 6, dispositionName: "CALL BACK" },
          { dispositionId: 7, dispositionName: "NOT CONNECTED" },
          { dispositionId: 8, dispositionName: "CALL DISCONNECTED" },
          { dispositionId: 9, dispositionName: "NOT INTERESTED" },
          { dispositionId: 10, dispositionName: "FUTURE CALL BACK" },
          { dispositionId: 11, dispositionName: "PRANK CALL" },
          { dispositionId: 12, dispositionName: "SWITCH OFF" },
        ]);
      }
    };
    fetchDispositions();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    fetchDashboardData();
  }, [dateRange]);

  const fmt = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getDateRangeDates = (range) => {
    const now = new Date();
    switch (range) {
      case "Today":
        return { startDate: fmt(now), endDate: fmt(now) };
      case "Yesterday": {
        const y = new Date(now);
        y.setDate(y.getDate() - 1);
        return { startDate: fmt(y), endDate: fmt(y) };
      }
      case "Last 7 Days": {
        const s = new Date(now);
        s.setDate(s.getDate() - 7);
        return { startDate: fmt(s), endDate: fmt(now) };
      }
      case "This Month": {
        const s = new Date(now.getFullYear(), now.getMonth(), 1);
        return { startDate: fmt(s), endDate: fmt(now) };
      }
      case "Custom Range":
      case "Custom":
        return {
          startDate: customStartDate || null,
          endDate: customEndDate || null,
        };
      default:
        return { startDate: null, endDate: null };
    }
  };

  const buildDispositionCards = (summaryData) => {
    const breakdown = summaryData?.dispositions;
    if (!Array.isArray(breakdown) || breakdown.length === 0) return [];
    return breakdown.map((item) => {
      const camelKey = item.dispositionName
        .toLowerCase()
        .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr) => chr.toUpperCase());
      const config = DISPOSITION_CONFIG[camelKey] || DEFAULT_CONFIG;
      return {
        label: item.dispositionName,
        status: item.dispositionName.replace(/ /g, "_"),
        value: item.count ?? 0,
        trend: item.percentage != null ? `${item.percentage}%` : "0%",
        dispositionId: item.dispositionId,
        icon: (
          <img
            src={config.icon}
            alt={item.dispositionName}
            className="w-[100px] h-[80px]"
          />
        ),
        theme: {
          bg: isDark ? "bg-white/5" : config.theme.bg,
          border: isDark ? "border-white/10" : config.theme.border,
          badge: "bg-transparent",
          shadow: isDark ? "shadow-black/20" : "shadow-slate-200",
          inner: isDark ? "" : config.theme.inner,
          ring: isDark ? "ring-white/20" : config.theme.ring,
        },
      };
    });
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const { startDate, endDate } = getDateRangeDates(dateRange);
      const user = JSON.parse(localStorage.getItem("user"));
      const employeeId = user?.id || userId;
      if (!employeeId) {
        setError("User not authenticated");
        setLoading(false);
        return;
      }
      const buildQ = () => {
        const params = new URLSearchParams();
        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);
        params.append("employeeId", employeeId);
        return `?${params.toString()}`;
      };
      const query = buildQ();
      const leadsParams = new URLSearchParams();
      leadsParams.append("employeeId", employeeId);
      leadsParams.append("page", currentPage - 1);
      leadsParams.append("size", itemsPerPage);
      if (startDate) leadsParams.append("startDate", startDate);
      if (endDate) leadsParams.append("endDate", endDate);

      const [summaryData, leadsData] = await Promise.all([
        dashboardApi.getLeadsSummary(query).catch(() => null),
        leadsApi.getAllLeads(`?${leadsParams.toString()}`).catch(() => []),
      ]);
      let filteredLeadsData = Array.isArray(leadsData)
        ? leadsData
        : Array.isArray(leadsData?.leads)
          ? leadsData.leads
          : Array.isArray(leadsData?.content)
            ? leadsData.content
            : [];
      setTotalItems(leadsData?.totalCount || filteredLeadsData.length);
      setLeads(filteredLeadsData);
      setDispositionCards(buildDispositionCards(summaryData));
      setStats({
        totalLeads: summaryData?.totalLeads,
        untaggedLeads: summaryData?.untaggedLeads || 0,
        conversionRate: summaryData?.conversionRate || "0%",
        activeToday: summaryData?.activeToday || 0,
        orderPlacedPercentage: (() => {
          const op = summaryData?.dispositions?.find(
            (d) => d.dispositionName === "ORDER PLACED",
          );
          return op ? `${op.percentage}%` : "0%";
        })(),
      });
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError(err.message || "Failed to load dashboard data.");
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (leadId, newStatus, dispositionId) => {
    try {
      await leadsApi.updateLeadDisposition(leadId, newStatus);
      setLeads(
        leads.map((lead) =>
          lead.id === leadId
            ? { ...lead, dispositionName: newStatus, dispositionId }
            : lead,
        ),
      );
      setOpenDropdownId(null);
      message.success("Status updated successfully");
    } catch (err) {
      message.error(
        "Failed to update status: " + (err.message || "Unknown error"),
      );
    }
  };

  const handleViewOrderDetails = async (lead) => {
    if (!lead.orderId) {
      message.warning("No order associated with this lead");
      return;
    }
    try {
      setLoadingOrderDetails(true);
      setShowOrderDetails(true);
      const orderDetails = await ordersApi.getOrderDetails(lead.orderId);
      setSelectedOrderDetails(orderDetails);
    } catch (err) {
      message.error("Failed to load order details");
      setShowOrderDetails(false);
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  // ✅ Lead to Order — dialer open karo (call ke baad /orders/add navigate hoga)
  const handleLeadToOrder = (lead) => {
    triggerDialer(lead);
    navigate("/orders/add", { state: { leadData: lead } });
    setOpenActionMenuId(null);
  };

  const handleLeadHistory = (lead) => console.log("View lead history:", lead);

  const getStatusColor = (dispositionId) => {
    const colors = isDark
      ? [
          "border-blue-500/30 bg-blue-500/10 text-blue-400",
          "border-green-500/30 bg-green-500/10 text-green-400",
          "border-yellow-500/30 bg-yellow-500/10 text-yellow-400",
          "border-orange-500/30 bg-orange-500/10 text-orange-400",
          "border-red-500/30 bg-red-500/10 text-red-400",
          "border-purple-500/30 bg-purple-500/10 text-purple-400",
          "border-teal-500/30 bg-teal-500/10 text-teal-400",
          "border-rose-500/30 bg-rose-500/10 text-rose-400",
          "border-amber-500/30 bg-amber-500/10 text-amber-400",
          "border-gray-500/30 bg-gray-500/10 text-gray-300",
        ]
      : [
          "border-blue-300 bg-blue-50 text-blue-700",
          "border-green-300 bg-green-50 text-green-700",
          "border-yellow-300 bg-yellow-50 text-yellow-700",
          "border-orange-300 bg-orange-50 text-orange-700",
          "border-red-300 bg-red-50 text-red-700",
          "border-purple-300 bg-purple-50 text-purple-700",
          "border-teal-300 bg-teal-50 text-teal-700",
          "border-rose-300 bg-rose-50 text-rose-700",
          "border-amber-300 bg-amber-50 text-amber-700",
          "border-gray-300 bg-gray-50 text-gray-700",
        ];
    if (!dispositionId)
      return isDark
        ? "border-gray-500/30 bg-gray-500/10 text-gray-300"
        : "border-gray-300 bg-gray-50 text-gray-700";
    return colors[(dispositionId - 1) % colors.length];
  };

  const currentLeads = leads.filter(
    (lead) =>
      lead.id?.toString().includes(searchTerm) ||
      lead.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.contactNumber?.includes(searchTerm),
  );

  const totalLeadsCalculated = stats?.totalLeads || 0;
  const conversationRateCalculated =
    stats?.orderPlacedPercentage || stats?.conversionRate || "0%";
  const activeTodayCount = stats?.activeToday || 0;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest(".status-dropdown") &&
        !event.target.closest(".action-menu")
      ) {
        setOpenDropdownId(null);
        setOpenActionMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const displayValue = (value) => {
    if (value === null || value === undefined || value === "" || value === 0)
      return "-/-";
    return value;
  };

  return (
    <div className="space-y-6 pt-4">
      {/* Header */}
      <div className="flex justify-between items-center bg-transparent mb-2">
        <div className="flex items-center gap-4">
          <h2
            className="text-[28px] font-black"
            style={{ color: currentTheme.color }}
          >
            Dashboard
          </h2>
          <div className="relative">
            <div
              onClick={() => setIsDateDropdownOpen(!isDateDropdownOpen)}
              className="flex items-center bg-white border border-gray-300 rounded-full px-4 py-1.5 shadow-sm cursor-pointer transition-all"
              style={{ hoverBorderColor: currentTheme.color }}
            >
              <CalendarClock className="w-5 h-5 text-orange-400 mr-2" />
              <span className="text-[15px] font-medium text-gray-400">
                {dateRange}
              </span>
              <ChevronDown
                className="w-4 h-4 ml-2"
                style={{ color: currentTheme.color }}
              />
            </div>
            {isDateDropdownOpen && (
              <div className="absolute left-0 top-full mt-2 w-48 bg-white rounded-2xl shadow-xl border border-gray-100 py-3 z-[100]">
                {[
                  "Today",
                  "Yesterday",
                  "Last 7 Days",
                  "This Month",
                  "All Time",
                  "Custom Range",
                ].map((option) => (
                  <button
                    key={option}
                    onClick={() => {
                      setIsDateDropdownOpen(false);
                      if (option === "Custom Range") {
                        setShowCustomDateModal(true);
                      } else {
                        setDateRange(option);
                      }
                    }}
                    className={`w-full px-5 py-2.5 text-left text-[14px] transition-colors hover:bg-teal-50 ${dateRange === option ? "text-teal-600 font-bold bg-teal-50" : "text-gray-600 font-medium"}`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <button
          className="p-2.5 bg-white border border-gray-200 rounded-full shadow-sm hover:bg-gray-50 transition-all"
          style={{ color: currentTheme.color }}
        >
          <Filter className="w-6 h-6" />
        </button>
      </div>

      {/* Summary Bar */}
      <div
        className="rounded-[16px] p-6 text-white flex flex-row items-center gap-12 shadow-md"
        style={{ backgroundColor: currentTheme.color }}
      >
        <div className="flex items-center gap-4">
          <div className="bg-white/20 p-3 rounded-2xl flex items-center justify-center">
            <Users className="w-6 h-6 text-white" />
          </div>
          <div className="flex flex-col">
            <span className="text-[28px] font-black leading-tight">
              {totalLeadsCalculated}
            </span>
            <p className="text-[12px] font-semibold opacity-90 leading-none">
              Total Leads
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="bg-white/20 p-3 rounded-2xl flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-white" />
          </div>
          <div className="flex flex-col">
            <span className="text-[28px] font-black leading-tight">
              {conversationRateCalculated}
            </span>
            <p className="text-[12px] font-semibold opacity-90 leading-none">
              Conversion Rate
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="bg-white/20 p-3 rounded-2xl flex items-center justify-center">
            <Clock className="w-6 h-6 text-white" />
          </div>
          <div className="flex flex-col">
            <span className="text-[28px] font-black leading-tight">
              {activeTodayCount}
            </span>
            <p className="text-[12px] font-semibold opacity-90 leading-none">
              Active Today
            </p>
          </div>
        </div>
      </div>

      {/* Section Heading */}
      <div className="pt-2">
        <h2
          className="text-[28px] font-black tracking-tight"
          style={{ color: currentTheme.color }}
        >
          Lead Status Breakdown
        </h2>
        <p className="text-[15px] text-gray-500 font-medium tracking-tight opacity-90">
          Real-time tracking and performance metrics
        </p>
      </div>

      {/* Disposition Cards */}
      <div className="flex gap-[40px] overflow-x-auto no-scrollbar scroll-smooth pt-6 pb-6 px-4">
        {dispositionCards.length > 0
          ? dispositionCards.map((stat, index) => (
              <div key={index} className="min-w-[180px]">
                <StatCard
                  stat={stat}
                  index={index}
                  onClick={() => handleStatClick(stat.status)}
                  isActive={selectedStatus === stat.status}
                />
              </div>
            ))
          : !loading && (
              <p className="text-gray-400 text-sm px-2">
                No disposition data available.
              </p>
            )}
      </div>

      {/* Search */}
      <div className="mb-6 flex items-center space-x-4">
        <h2 className="text-2xl font-bold text-gray-800">Lead</h2>
        <ExpandableSearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          placeholder="Search by lead id or customer name"
        />
      </div>

      {/* Leads Table */}
      <div
        className={`rounded-xl shadow-sm overflow-hidden ${isDark ? "bg-[#1e293b]" : "bg-white"}`}
      >
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <Loader
                className="w-8 h-8 animate-spin mx-auto mb-4"
                style={{ color: currentTheme.color }}
              />
              <p className="text-gray-600">Loading dashboard data...</p>
            </div>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead
                  className="text-white"
                  style={{ background: currentTheme.color }}
                >
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">
                      LEAD ID
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">
                      DATE
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">
                      NAME
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">
                      PRODUCT
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">
                      CATEGORY
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">
                      PAY MODE
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">
                      AMOUNT
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">
                      LEAD STATUS
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">
                      REMARKS
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">
                      ACTION
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">
                      COURIER
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">
                      AWB NUMBER
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={`divide-y ${isDark ? "divide-white/10" : "divide-gray-200"}`}
                >
                  {currentLeads.length > 0 ? (
                    currentLeads.map((lead) => (
                      <tr
                        key={lead.id}
                        className={`transition-colors ${isDark ? "hover:bg-white/5" : "hover:bg-gray-50"}`}
                      >
                        <td
                          className={`px-4 py-4 text-sm font-medium ${isDark ? "text-gray-200" : "text-gray-900"}`}
                        >
                          {lead.id}
                        </td>
                        <td
                          className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}
                        >
                          {lead.createdAt ? formatDate(lead.createdAt) : "-"}
                        </td>

                        {/* ✅ NAME + PHONE — phone click se call lagegi */}
                        <td className="px-4 py-4">
                          <div className="flex flex-col">
                            <span
                              className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}
                            >
                              {lead.customerName}
                            </span>
                            <button
                              onClick={() => triggerDialer(lead)}
                              className={`flex items-center space-x-1 text-xs transition-colors group w-fit mt-0.5 ${isDark ? "text-gray-400" : "text-gray-500"}`}
                              style={{ "--hover-color": currentTheme.color }}
                              onMouseEnter={(e) =>
                                (e.currentTarget.style.color =
                                  currentTheme.color)
                              }
                              onMouseLeave={(e) =>
                                (e.currentTarget.style.color = "")
                              }
                              title={`Call ${lead.customerName}`}
                            >
                              <span
                                className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center transition-colors flex-shrink-0"
                                style={{ backgroundColor: currentTheme.color }}
                              >
                                <Phone className="w-2.5 h-2.5 text-white" />
                              </span>
                              <span className="group-hover:underline">
                                {lead.contactNumber}
                              </span>
                            </button>
                          </div>
                        </td>

                        <td
                          className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}
                        >
                          {lead.product || "-"}
                        </td>
                        <td
                          className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}
                        >
                          {lead.campaignName || "-"}
                        </td>
                        <td
                          className={`px-4 py-4 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}
                        >
                          {lead.paymentMode || "-"}
                        </td>
                        <td className="px-4 py-4 text-sm font-semibold text-green-500">
                          {lead.amount ? `₹ ${lead.amount}` : "-"}
                        </td>

                        {/* Lead Status Dropdown */}
                        <td className="px-4 py-4 relative status-dropdown">
                          <button
                            onClick={() =>
                              setOpenDropdownId(
                                openDropdownId === lead.id ? null : lead.id,
                              )
                            }
                            className={`w-full px-3 py-2 rounded-lg text-xs font-medium border flex items-center justify-between ${getStatusColor(lead.dispositionId)}`}
                          >
                            <span>
                              {lead.dispositionName || "Select Status"}
                            </span>
                            <ChevronDown className="w-4 h-4" />
                          </button>
                          {openDropdownId === lead.id && (
                            <div
                              className={`absolute left-0 top-full mt-1 rounded-lg shadow-lg border py-2 w-56 max-h-48 overflow-y-auto z-10 ${isDark ? "bg-[#0f172a] border-white/10" : "bg-white border-gray-200"}`}
                            >
                              {leadStatuses.length > 0 ? (
                                leadStatuses.map((disposition) => (
                                  <button
                                    key={
                                      disposition.dispositionId ||
                                      disposition.id
                                    }
                                    onClick={() =>
                                      handleStatusChange(
                                        lead.id,
                                        disposition.dispositionName,
                                        disposition.dispositionId ||
                                          disposition.id,
                                      )
                                    }
                                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 text-gray-700"
                                  >
                                    {disposition.dispositionName}
                                  </button>
                                ))
                              ) : (
                                <div className="px-4 py-2 text-sm text-gray-400">
                                  Loading...
                                </div>
                              )}
                            </div>
                          )}
                        </td>

                        <td className="px-4 py-4 text-sm text-gray-700">
                          {lead.remarks === "stdtwyteyytwe"
                            ? "-"
                            : lead.remarks || "-"}
                        </td>

                        {/* Action Menu */}
                        <td className="px-4 py-4 relative action-menu">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() =>
                                setOpenActionMenuId(
                                  openActionMenuId === lead.id ? null : lead.id,
                                )
                              }
                              className={`p-1.5 rounded-lg transition-colors ${isDark ? "hover:bg-white/10" : "hover:bg-gray-50"}`}
                            >
                              <Eye
                                className="w-5 h-5"
                                style={{ color: currentTheme.color }}
                              />
                            </button>
                            {openActionMenuId === lead.id && (
                              <div
                                className={`absolute left-0 top-full mt-1 rounded-lg shadow-lg border py-2 w-48 z-20 ${isDark ? "bg-[#0f172a] border-white/10" : "bg-white border-gray-200"}`}
                              >
                                {/* ✅ Lead to order — dialer open karega */}
                                <button
                                  onClick={() => handleLeadToOrder(lead)}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 text-gray-700 flex items-center space-x-2"
                                >
                                  <FileText
                                    className="w-4 h-4"
                                    style={{ color: currentTheme.color }}
                                  />
                                  <span>Lead to order</span>
                                </button>
                                <button
                                  onClick={() => {
                                    handleLeadHistory(lead);
                                    setOpenActionMenuId(null);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 text-gray-700 flex items-center space-x-2"
                                >
                                  <History className="w-4 h-4 text-blue-600" />
                                  <span>Lead History</span>
                                </button>
                              </div>
                            )}
                          </div>
                        </td>

                        <td className="px-4 py-4 text-sm text-gray-700">-</td>
                        <td className="px-4 py-4 text-sm text-gray-700">-</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="12"
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        {error
                          ? "Failed to load leads. Please try again."
                          : "No leads found"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div
              className={`flex items-center justify-between px-6 py-4 border-t ${isDark ? "bg-[#1e293b] border-white/10" : "bg-gray-50 border-gray-200"}`}
            >
              <div className="text-sm text-gray-600"></div>
              <div className="flex items-center space-x-2">
                <Pagination
                  currentPage={currentPage}
                  totalItems={totalItems}
                  itemsPerPage={itemsPerPage}
                  onPageChange={setCurrentPage}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {/* Order Details Modal */}
      {showOrderDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div
              className="px-6 py-4 flex items-center justify-between border-b"
              style={{ backgroundColor: `${currentTheme.color}20` }}
            >
              <h2 className="text-xl font-bold text-gray-800">Order Details</h2>
              <button
                onClick={() => setShowOrderDetails(false)}
                className="p-1 hover:bg-white hover:bg-opacity-50 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
              {loadingOrderDetails ? (
                <div className="flex items-center justify-center py-20">
                  <Loader className="w-8 h-8 animate-spin text-teal-500" />
                </div>
              ) : selectedOrderDetails ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Order ID</p>
                      <p className="text-sm font-semibold">
                        {displayValue(selectedOrderDetails.order?.orderId)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Order Date</p>
                      <p className="text-sm font-semibold">
                        {selectedOrderDetails.order?.orderDate
                          ? formatDate(selectedOrderDetails.order.orderDate)
                          : "-/-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Product</p>
                      <p className="text-sm font-semibold">
                        {displayValue(selectedOrderDetails.order?.product)}
                      </p>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-3">Customer Details</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Name</p>
                        <p className="text-sm font-medium">
                          {displayValue(
                            selectedOrderDetails.lead?.customerName,
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">
                          Contact Number
                        </p>
                        <p className="text-sm font-medium">
                          {displayValue(
                            selectedOrderDetails.lead?.contactNumber,
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Email</p>
                        <p className="text-sm font-medium">
                          {displayValue(selectedOrderDetails.lead?.email)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Address</p>
                        <p className="text-sm font-medium">
                          {displayValue(selectedOrderDetails.lead?.address)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-3">Order Summary</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Sub Total</span>
                        <span className="font-semibold">
                          {selectedOrderDetails.order?.subTotal
                            ? formatCurrency(
                                selectedOrderDetails.order.subTotal,
                              )
                            : "₹0"}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Prepaid Amount</span>
                        <span className="font-semibold text-green-600">
                          {selectedOrderDetails.order?.prepaidAmount
                            ? formatCurrency(
                                selectedOrderDetails.order.prepaidAmount,
                              )
                            : "₹0"}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Discount</span>
                        <span className="font-semibold text-green-600">
                          {selectedOrderDetails.order?.discount
                            ? formatCurrency(
                                selectedOrderDetails.order.discount,
                              )
                            : "₹0"}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Shipping Charges</span>
                        <span className="font-semibold text-emerald-600">
                          {selectedOrderDetails.order?.shippingCharges === 0
                            ? "Free"
                            : formatCurrency(
                                selectedOrderDetails.order?.shippingCharges ||
                                  0,
                              )}
                        </span>
                      </div>
                      <div className="pt-3 border-t border-gray-200">
                        <div className="flex justify-between">
                          <span className="text-base font-bold">Amount</span>
                          <span
                            className="text-xl font-bold"
                            style={{ color: currentTheme.color }}
                          >
                            {formatCurrency(
                              Math.max(
                                0,
                                (selectedOrderDetails.order?.grandTotal || 0) -
                                  (selectedOrderDetails.order?.prepaidAmount ||
                                    0),
                              ),
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Payment Mode</p>
                      <p className="text-sm font-semibold">
                        {displayValue(selectedOrderDetails.order?.paymentMode)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">
                        Shipping Method
                      </p>
                      <p className="text-sm font-semibold">
                        {displayValue(
                          selectedOrderDetails.order?.shippingMethod,
                        )}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Order Status</p>
                      <span className="inline-block px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                        {displayValue(
                          selectedOrderDetails.order?.approvalStatus,
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-20 text-gray-500">
                  No order details available
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {/* ── Custom Date Modal ── */}
      {showCustomDateModal && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowCustomDateModal(false);
          }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-scaleIn">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">
                Select Custom Date Range
              </h3>
              <button
                onClick={() => setShowCustomDateModal(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date – End Date
              </label>
              <RangePicker
                value={
                  customStartDate && customEndDate
                    ? [dayjs(customStartDate), dayjs(customEndDate)]
                    : null
                }
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setCustomStartDate(dates[0].format("YYYY-MM-DD"));
                    setCustomEndDate(dates[1].format("YYYY-MM-DD"));
                  } else {
                    setCustomStartDate("");
                    setCustomEndDate("");
                  }
                }}
                format="DD-MM-YYYY"
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  borderRadius: "10px",
                }}
                className="w-full text-base"
                size="large"
              />
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCustomDateModal(false)}
                className="px-5 py-2 text-sm font-semibold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (!customStartDate || !customEndDate) {
                    message.warning("Please select both start and end date");
                    return;
                  }
                  setDateRange("Custom Range");
                  setShowCustomDateModal(false);
                  setCurrentPage(1);
                }}
                disabled={!customStartDate || !customEndDate}
                className="px-5 py-2 text-sm font-semibold text-white rounded-xl transition-colors hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: currentTheme.color }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
